// Menu-only gamepad poller. Separate instance from `src/core/Gamepad.ts`
// because the game's Gamepad is driven by Input.poll() which only runs
// inside the GameLoop — that doesn't fire on splash / home / map-select.
// This class polls via its own rAF loop while a MenuNav is mounted.
//
// Edges only. Directional inputs (dpad + left stick past threshold) emit
// one edge per cross-from-rest; A button fires one activate edge per press.
// The downstream MenuNav translates those into focus/activate calls, so
// there's no per-frame "held" state to worry about here.

export type MenuGamepadDirection = 'up' | 'down' | 'left' | 'right';

export interface MenuGamepadHandlers {
  onDirection(dir: MenuGamepadDirection): void;
  onActivate(): void;
  /** Optional B-button (Xbox B / PlayStation ◯) edge — used as a "back" /
   *  "cancel" affordance. Screens that have a meaningful back action wire
   *  this; everywhere else B is a no-op. */
  onCancel?: () => void;
}

export interface MenuGamepadOptions {
  /** When true, holding a direction past the initial delay re-emits
   *  onDirection at a steady cadence — for menus with long lists where
   *  one-edge-per-flick navigation is impractical. */
  holdToRepeat?: boolean;
}

// Stick/dpad thresholds. PUSH must be crossed to fire an edge; stick must
// drop below RELEASE before another edge can arm. Matches the hysteresis
// in Input.ts for spectate-cycle so sideways diagonal walks don't spam.
const PUSH = 0.55;
const RELEASE = 0.30;

// Hold-to-repeat timings (gated by holdToRepeat option). After holding a
// direction past INITIAL_DELAY_MS we emit a fresh edge every REPEAT_MS so
// long lists (the language picker) can be scrolled without clicking once
// per row. Numbers tuned to match desktop OS key-repeat: 400ms before the
// first repeat, ~14 Hz after that.
const HOLD_INITIAL_DELAY_MS = 400;
const HOLD_REPEAT_MS = 70;

// Standard Mapping button indices we care about.
const BTN_A = 0;
const BTN_B = 1;
const BTN_DPAD_UP = 12;
const BTN_DPAD_DOWN = 13;
const BTN_DPAD_LEFT = 14;
const BTN_DPAD_RIGHT = 15;

// Left-stick axes.
const AX_LX = 0;
const AX_LY = 1;

export class MenuGamepad {
  private handlers: MenuGamepadHandlers;
  private holdToRepeat: boolean;
  private rafHandle = 0;
  private running = false;
  // Latched state. Written on edge-press, cleared when the input returns
  // below RELEASE (for sticks) or the button releases (for A/dpad).
  private latchedX: -1 | 0 | 1 = 0;
  private latchedY: -1 | 0 | 1 = 0;
  private prevA = false;
  private prevB = false;
  private prevDpad: Record<MenuGamepadDirection, boolean> = {
    up: false, down: false, left: false, right: false
  };
  // Hold-to-repeat bookkeeping (only consulted when holdToRepeat=true).
  // `firstAt` is the timestamp the direction was first latched; `lastAt`
  // is the timestamp of the most recent edge (initial press or repeat).
  private holdX: { firstAt: number; lastAt: number } | null = null;
  private holdY: { firstAt: number; lastAt: number } | null = null;
  private holdDpad: Record<MenuGamepadDirection, { firstAt: number; lastAt: number } | null> = {
    up: null, down: null, left: null, right: null
  };

  constructor(handlers: MenuGamepadHandlers, opts: MenuGamepadOptions = {}) {
    this.handlers = handlers;
    this.holdToRepeat = opts.holdToRepeat ?? false;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    // Seed the "previous" state from whatever is currently held on the pad
    // before running the first tick. Without this, mounting a menu while a
    // button/stick is still physically depressed (e.g. the A press that
    // activated the previous menu hasn't been released yet) would register
    // as a fresh edge on the first tick and fire onActivate / onDirection
    // against the new menu immediately. With seeding, we only see edges for
    // presses that *start* after the menu mounts.
    const pad = this.pickPad();
    if (pad) {
      this.prevA = isPressed(pad, BTN_A);
      this.prevB = isPressed(pad, BTN_B);
      this.prevDpad.up    = isPressed(pad, BTN_DPAD_UP);
      this.prevDpad.down  = isPressed(pad, BTN_DPAD_DOWN);
      this.prevDpad.left  = isPressed(pad, BTN_DPAD_LEFT);
      this.prevDpad.right = isPressed(pad, BTN_DPAD_RIGHT);
      const lx = pad.axes[AX_LX] ?? 0;
      const ly = pad.axes[AX_LY] ?? 0;
      this.latchedX = lx > PUSH ? 1 : lx < -PUSH ? -1 : 0;
      this.latchedY = ly > PUSH ? 1 : ly < -PUSH ? -1 : 0;
    }
    this.tick();
  }

  stop(): void {
    if (!this.running) return;
    this.running = false;
    if (this.rafHandle) cancelAnimationFrame(this.rafHandle);
    this.rafHandle = 0;
    // Reset latch so re-start after a pause doesn't treat a currently-held
    // stick as a fresh edge.
    this.latchedX = 0;
    this.latchedY = 0;
    this.prevA = false;
    this.prevB = false;
    this.prevDpad.up = this.prevDpad.down = this.prevDpad.left = this.prevDpad.right = false;
    this.holdX = null;
    this.holdY = null;
    this.holdDpad.up = this.holdDpad.down = this.holdDpad.left = this.holdDpad.right = null;
  }

  private tick = (): void => {
    if (!this.running) return;
    this.rafHandle = requestAnimationFrame(this.tick);

    const pad = this.pickPad();
    if (!pad) return;

    const now = performance.now();

    // --- Left stick: axis → directional edges with hysteresis.
    const lx = pad.axes[AX_LX] ?? 0;
    const ly = pad.axes[AX_LY] ?? 0;

    if (lx > PUSH && this.latchedX !== 1) {
      this.latchedX = 1;
      this.holdX = this.holdToRepeat ? { firstAt: now, lastAt: now } : null;
      this.handlers.onDirection('right');
    } else if (lx < -PUSH && this.latchedX !== -1) {
      this.latchedX = -1;
      this.holdX = this.holdToRepeat ? { firstAt: now, lastAt: now } : null;
      this.handlers.onDirection('left');
    } else if (Math.abs(lx) < RELEASE) {
      this.latchedX = 0;
      this.holdX = null;
    } else if (this.holdToRepeat && this.holdX && this.latchedX !== 0
               && now - this.holdX.firstAt > HOLD_INITIAL_DELAY_MS
               && now - this.holdX.lastAt > HOLD_REPEAT_MS) {
      this.holdX.lastAt = now;
      this.handlers.onDirection(this.latchedX === 1 ? 'right' : 'left');
    }

    if (ly > PUSH && this.latchedY !== 1) {
      this.latchedY = 1;
      this.holdY = this.holdToRepeat ? { firstAt: now, lastAt: now } : null;
      this.handlers.onDirection('down');
    } else if (ly < -PUSH && this.latchedY !== -1) {
      this.latchedY = -1;
      this.holdY = this.holdToRepeat ? { firstAt: now, lastAt: now } : null;
      this.handlers.onDirection('up');
    } else if (Math.abs(ly) < RELEASE) {
      this.latchedY = 0;
      this.holdY = null;
    } else if (this.holdToRepeat && this.holdY && this.latchedY !== 0
               && now - this.holdY.firstAt > HOLD_INITIAL_DELAY_MS
               && now - this.holdY.lastAt > HOLD_REPEAT_MS) {
      this.holdY.lastAt = now;
      this.handlers.onDirection(this.latchedY === 1 ? 'down' : 'up');
    }

    // --- Dpad: button → directional edges on press.
    this.checkDpad(pad, BTN_DPAD_UP, 'up', now);
    this.checkDpad(pad, BTN_DPAD_DOWN, 'down', now);
    this.checkDpad(pad, BTN_DPAD_LEFT, 'left', now);
    this.checkDpad(pad, BTN_DPAD_RIGHT, 'right', now);

    // --- A button: activate edge.
    const aDown = isPressed(pad, BTN_A);
    if (aDown && !this.prevA) this.handlers.onActivate();
    this.prevA = aDown;

    // --- B button: cancel / back edge. Only fires when the active menu
    // wired an onCancel — screens without a back action see B as a no-op.
    const bDown = isPressed(pad, BTN_B);
    if (bDown && !this.prevB) this.handlers.onCancel?.();
    this.prevB = bDown;
  };

  private checkDpad(pad: globalThis.Gamepad, index: number, dir: MenuGamepadDirection, now: number): void {
    const down = isPressed(pad, index);
    if (down && !this.prevDpad[dir]) {
      this.handlers.onDirection(dir);
      if (this.holdToRepeat) this.holdDpad[dir] = { firstAt: now, lastAt: now };
    } else if (down && this.holdToRepeat) {
      const hold = this.holdDpad[dir];
      if (hold && now - hold.firstAt > HOLD_INITIAL_DELAY_MS
          && now - hold.lastAt > HOLD_REPEAT_MS) {
        hold.lastAt = now;
        this.handlers.onDirection(dir);
      }
    } else if (!down) {
      this.holdDpad[dir] = null;
    }
    this.prevDpad[dir] = down;
  }

  private pickPad(): globalThis.Gamepad | null {
    const pads = navigator.getGamepads?.() ?? [];
    // Most-recently-updated connected pad wins. Matches the game's
    // Gamepad.pickActivePad selection policy so both instances track the
    // same physical controller even if multiple are attached.
    let best: globalThis.Gamepad | null = null;
    let newestTs = -1;
    for (const p of pads) {
      if (!p || !p.connected) continue;
      if (p.timestamp > newestTs) { newestTs = p.timestamp; best = p; }
    }
    return best;
  }
}

function isPressed(pad: globalThis.Gamepad, index: number): boolean {
  const b = pad.buttons[index];
  if (!b) return false;
  return b.pressed || b.value > 0.5;
}
